/**
 * Small shared pieces of the Message builder screens: status chips, the
 * failure banner (says what the server refused, keeps the author's input), and
 * loading skeletons sized like the content they stand in for.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Banner, Btn, Icon, IconButton, SearchPickerDialog, Skeleton, StatusPill, Tooltip } from "@v0y4ge/jrni-ui";
import { useIsMobile } from "../../../../../shell/useResponsive";
import type { Failure } from "../api";
import type { Chip, Tr } from "../model";

/** The one control height of the builder's bars (header, toolbar): the design system's standard 36px (Btn size="md",
 *  a 36px IconButton, a SegmentedControl at its own default track), as on the other Studio screens. */
export const CONTROL_H = 36; // ui-config
/** The builder-css phone breakpoint, where the page is the one scroller. */
const PHONE_BP = 640; // ui-config
/** The action bar's height (one row of 36px buttons in 8px padding), for skeletons that stand in for it. */
const FOOTER_BUTTON_H = 36; // ui-config

export function Chips({ chips }: { chips: Chip[] }) {
  return <>{chips.map((c) => <StatusPill key={c.label} tone={c.tone} size="sm">{c.label}</StatusPill>)}</>;
}

/** The author-facing explanation of a refused read or write. The server's own message is shown when it has one. */
export function failureText(f: Failure, t: Tr): { title: string; text: string } {
  const server = f.message;
  switch (f.kind) {
    case "conflict":
      return { title: t("messaging.builder.fail.conflict", "Someone else changed this first"), text: t("messaging.builder.fail.conflictText", "The latest saved version has been loaded. Your edits are still here; review them and save again.") };
    case "invalid":
      return { title: t("messaging.builder.fail.invalid", "The server's checks refused this"), text: server || t("messaging.builder.fail.invalidText", "Fix the problems listed and try again. Your edits are kept.") };
    case "unsupported":
      return { title: t("messaging.builder.fail.unsupported", "This message can't hold that change"), text: server || t("messaging.builder.fail.unsupportedText", "This kind of message doesn't support the field you changed. Your edits are kept.") };
    case "readOnly":
      return { title: t("messaging.builder.fail.readOnly", "This is provided for you and can't be changed"), text: server || t("messaging.builder.fail.readOnlyText", "Nothing was changed. Make your own copy to edit it.") };
    case "usage":
      return { title: t("messaging.builder.fail.usage", "Messages depend on this layout"), text: server || t("messaging.builder.fail.usageText", "Nothing was changed. See which messages use it.") };
    case "selection":
      return { title: t("messaging.builder.fail.selection", "This layout can't be the global default"), text: server || t("messaging.builder.fail.selectionText", "Only a published layout that isn't archived can be the global default.") };
    case "forbidden":
      return { title: t("messaging.builder.fail.forbidden", "You don't have permission to do this"), text: t("messaging.builder.fail.forbiddenText", "Ask an administrator for message template access.") };
    case "notFound":
      return { title: t("messaging.builder.fail.notFound", "This no longer exists"), text: t("messaging.builder.fail.notFoundText", "It may have been removed. Reload the list.") };
    case "idempotency":
      return { title: t("messaging.builder.fail.idempotency", "This request was already used for a different change"), text: t("messaging.builder.fail.idempotencyText", "Try the action again. Your edits are kept.") };
    case "transient":
      return { title: t("messaging.builder.fail.transient", "Couldn't reach the server"), text: t("messaging.builder.fail.transientText", "Check your connection and try again. Retrying repeats the same request, so nothing is applied twice.") };
    default:
      return { title: t("messaging.builder.fail.generic", "Something went wrong"), text: server || t("messaging.builder.fail.genericText", "Nothing was changed. Try again.") };
  }
}

export function FailureBanner({ failure, t, onRetry, retryLabel, onDismiss, children }: {
  failure: Failure; t: Tr; onRetry?: () => void; retryLabel?: string; onDismiss?: () => void; children?: ReactNode;
}) {
  const copy = failureText(failure, t);
  const tone = failure.kind === "conflict" || failure.kind === "usage" || failure.kind === "transient" ? "warning" : "danger";
  return (
    <Banner tone={tone} size="compact" title={copy.title} onDismiss={onDismiss}
      action={onRetry ? <Btn size="sm" variant="secondary" onClick={onRetry}>{retryLabel || t("messaging.builder.retry", "Try again")}</Btn> : undefined}>
      {copy.text}
      {children}
    </Banner>
  );
}

export interface PlaceholderOption { value: string; label: string; supportingText?: string; disabled?: boolean }

/**
 * The Placeholders picker: a small button that opens the design system's searchable picker, wide enough to show each
 * placeholder's name and its token on one line. Picking one inserts it where the author's cursor last was (the caller's
 * `onPick`); the dialog closes first, so the insertion's own focus move is the last one. `iconOnly` (the phone toolbar)
 * shows the same button as a 32px icon with a tooltip, so the toolbar keeps two rows.
 */
export function PlaceholderPicker({ t, options, onPick, disabled, iconOnly }: { t: Tr; options: PlaceholderOption[]; onPick: (id: string) => void; disabled?: boolean; iconOnly?: boolean }) {
  const [open, setOpen] = useState(false);
  const items = useMemo(() => options.map((o) => ({ id: o.value, title: o.label, subtitle: o.supportingText, disabled: o.disabled,
    searchText: `${o.label} ${o.supportingText ?? ""} ${o.value}` })), [options]);
  const label = t("messaging.builder.placeholder.aria", "Placeholders: insert at the last cursor position");
  return (
    <>
      {iconOnly ? (
        <Tooltip content={t("messaging.builder.placeholders", "Placeholders")} position="bottom">
          <IconButton size={CONTROL_H} label={label} disabled={disabled || !options.length} aria-haspopup="dialog" onClick={() => setOpen(true)} testid="msgb-placeholders">
            <Icon.tags size={16} />
          </IconButton>
        </Tooltip>
      ) : (
        <Btn size="md" variant="secondary" hasCaret disabled={disabled || !options.length} aria-haspopup="dialog" aria-label={label}
          onClick={() => setOpen(true)} testid="msgb-placeholders">
          {t("messaging.builder.placeholders", "Placeholders")}
        </Btn>
      )}
      <SearchPickerDialog open={open} onClose={() => setOpen(false)} title={t("messaging.builder.placeholders", "Placeholders")} description={label}
        items={items} onPick={(id) => { setOpen(false); setTimeout(() => onPick(id), 0); }}
        searchPlaceholder={t("messaging.builder.placeholder.search", "Search name or token")}
        emptyState={<p className="msgb-small">{t("messaging.builder.placeholder.none", "No placeholders match")}</p>}
        listMaxHeight="min(420px, 56vh)" testid="msgb-placeholder-picker" />
    </>
  );
}

/**
 * Phone (640px and below): the page scrolls as one column, so the email builder uses its "flow" frame there (no height of
 * its own, the canvas grows to the email) and the page is the only scroller. Elsewhere the builder is "flush" and fills
 * the editor column. Returns the frame to pass to EmailBuilder.
 */
export function useBuilderFrame(): "flush" | "flow" {
  return useIsMobile(PHONE_BP) ? "flow" : "flush";
}
/** Below this width the builder toolbar keeps one row of standard 36px controls by showing Placeholders as an icon
 *  (with its tooltip and accessible name) instead of a labelled button. */
const PLACEHOLDER_ICON_BP = 1599; // ui-config
export function usePlaceholderIconOnly(): boolean {
  return useIsMobile(PLACEHOLDER_ICON_BP);
}

/** One rail row: the 52px MediaRow rhythm (title line + subtitle line). */
export function RowSkeleton() {
  return <div className="msgb-skel-row"><Skeleton variant="text" width="62%" height={13} /><Skeleton variant="text" width="38%" height={11} /></div>;
}

export function RailSkeleton({ rows, label }: { rows: number; label: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="msgb-sr">{label}</span>
      {Array.from({ length: rows }, (_, i) => <RowSkeleton key={i} />)}
    </div>
  );
}

/** The toolbar strip of the compact email builder: view switch, placeholder slot, undo/redo/add, device and zoom. */
const TOOLBAR_SKELETON = [176, 120, 140, 290]; // ui-config: skeleton widths in px (view switch, placeholders, edit group, preview group)

/**
 * The editor while a row loads, shaped like the ready editor so nothing jumps: the header row (title, meta line,
 * channel switch), the settings strip, then the builder (toolbar strip, canvas and the 320px side panel) filling the
 * rest. `withHeader={false}` when the real header is already shown above it; `lead` (the list trigger on a tablet or
 * phone) is shown first in the skeleton's header row.
 */
export function EditorSkeleton({ label, withHeader = true, lead }: { label: string; withHeader?: boolean; lead?: ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <span className="msgb-sr">{label}</span>
      {withHeader ? (
        <div className="msgb-edhead">
          {lead}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)", minWidth: 0 }}>
            <Skeleton variant="text" width="40%" height={16} />
            <Skeleton variant="text" width="24%" height={12} />
          </div>
          <div style={{ flex: "none" }}><Skeleton variant="rect" width={120} height={CONTROL_H} /></div>
        </div>
      ) : null}
      <div className="msgb-edbody"><Skeleton variant="text" width="30%" height={20} /></div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: "none", display: "flex", alignItems: "center", gap: "var(--jrni-space-2)", height: 52, padding: "0 var(--jrni-space-3)", borderBottom: "1px solid var(--jrni-color-surface-border)", overflow: "hidden" }}>
          {TOOLBAR_SKELETON.map((w, i) => <div key={i} style={{ flex: "0 1 auto", minWidth: 0, marginLeft: i === 2 ? "auto" : undefined }}><Skeleton variant="rect" width={w} height={CONTROL_H} /></div>)}
        </div>
        <div className="msgb-skel-builder" style={{ flex: 1, minHeight: 420, display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: "var(--jrni-space-3)", padding: "var(--jrni-space-3)" }}>
          <Skeleton variant="rect" width="100%" height="100%" />
          <Skeleton variant="rect" width="100%" height="100%" />
        </div>
      </div>
    </div>
  );
}

/** The action bar while a row loads, shaped like the ready one (history, the note, then the save and publish buttons),
 *  so the bar doesn't appear and push the page when the row arrives. */
export function FooterSkeleton() {
  return (
    <div className="msgb-footer" aria-hidden="true">
      <div className="msgb-footrow">
        <div className="msgb-footlead"><Skeleton variant="rect" width={132} height={FOOTER_BUTTON_H} /><span className="msgb-hide-phone" style={{ flex: 1, minWidth: 0 }}><Skeleton variant="text" width="60%" height={12} /></span></div>
        <div className="msgb-footend"><Skeleton variant="rect" width={104} height={FOOTER_BUTTON_H} /><Skeleton variant="rect" width={104} height={FOOTER_BUTTON_H} /></div>
      </div>
    </div>
  );
}

/** The SMS editor while its row loads, shaped like the ready one: a settings line, then the text field (with its
 *  placeholder button) beside the 320-420px phone preview. */
export function SmsSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <span className="msgb-sr">{label}</span>
      <div className="msgb-edbody"><Skeleton variant="text" width="22%" height={16} /><Skeleton variant="text" width="48%" height={12} /></div>
      <div className="msgb-two">
        <div className="msgb-stack">
          <div className="msgb-fieldbar"><Skeleton variant="text" width={72} height={14} /><Skeleton variant="rect" width={120} height={CONTROL_H} /></div>
          <Skeleton variant="rect" width="100%" height={148} />
          <Skeleton variant="text" width="40%" height={12} />
        </div>
        <div className="msgb-phone"><Skeleton variant="text" width="50%" height={12} /><Skeleton variant="rect" width="78%" height={64} /></div>
      </div>
    </div>
  );
}

/** A fully sized preview while the server renders. Without a height it fills its parent. */
export function PreviewSkeleton({ label, height }: { label: string; height?: number }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" style={height == null
      ? { flex: 1, minHeight: 420, height: "100%", display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)", padding: "var(--jrni-space-3)" }
      : { display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }}>
      <span className="msgb-sr">{label}</span>
      <Skeleton variant="rect" width="100%" height={height == null ? "100%" : height} />
    </div>
  );
}
